import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';

const INITIAL_REQUESTS = [
  { id: 1, name: 'Samuel Boateng', type: 'Annual Leave', dates: '12 Oct - 20 Oct 2023', days: 7, status: 'Approved', avatar: 'https://i.pravatar.cc/150?img=12' },
  { id: 2, name: 'Grace Asante', type: 'Sick Leave', dates: '24 Oct - 25 Oct 2023', days: 2, status: 'Pending', avatar: 'https://i.pravatar.cc/150?img=44' },
  { id: 3, name: 'Kwesi Mensah', type: 'Paternity Leave', dates: '01 Nov - 14 Nov 2023', days: 10, status: 'Pending', avatar: 'https://i.pravatar.cc/150?img=11' },
];

const LeaveManagement = () => {
  const [requests, setRequests] = useState(INITIAL_REQUESTS);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    type: 'Annual Leave',
    startDate: '',
    endDate: '',
    reason: ''
  });

  const handleRequestLeave = (e) => {
    e.preventDefault();
    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

    const newReq = {
      id: Date.now(),
      name: 'Samuel Boateng', 
      type: formData.type,
      dates: `${formData.startDate} - ${formData.endDate}`,
      days: diffDays,
      status: 'Pending',
      avatar: 'https://i.pravatar.cc/150?img=12'
    };
    setRequests([newReq, ...requests]);
    setShowModal(false);
    setFormData({ type: 'Annual Leave', startDate: '', endDate: '', reason: '' });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Leave & Absence</h1>
          <p className="text-gray-500 text-sm">Manage time off requests and balances.</p>
        </div>
        <button 
          onClick={() => setShowModal(true)}
          className="bg-[#11469A] text-white px-6 py-2.5 rounded-lg font-bold text-sm shadow-sm hover:bg-[#0d3678] transition-all flex items-center gap-2"
        >
          <SafeIcon name="Plus" size={18} /> Request Leave
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-4 bg-gray-50 border-b border-gray-100 font-bold text-xs uppercase tracking-widest text-gray-400">
            Recent Requests
          </div>
          <div className="divide-y divide-gray-100">
            {requests.map(req => (
              <div key={req.id} className="p-4 flex items-center justify-between hover:bg-gray-50/50 transition-colors">
                <div className="flex items-center gap-4">
                  <img src={req.avatar} className="w-10 h-10 rounded-full border border-gray-100" alt="" />
                  <div>
                    <h4 className="font-bold text-sm text-gray-800">{req.name}</h4>
                    <p className="text-xs text-gray-500">{req.type} • {req.dates} ({req.days} days)</p>
                  </div>
                </div>
                <span className={`px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wider ${
                  req.status === 'Approved' ? 'bg-green-50 text-green-600' : 'bg-yellow-50 text-yellow-600'
                }`}>
                  {req.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="card p-6 bg-[#11469A] text-white space-y-6 shadow-lg">
          <h3 className="font-bold text-lg border-b border-white/10 pb-4">Your Balances</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-xs font-bold uppercase tracking-widest opacity-80 mb-2">
                <span>Annual Leave</span>
                <span>14 / 21 Days</span>
              </div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-[#00AEEF] w-[66%]" />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-xs font-bold uppercase tracking-widest opacity-80 mb-2">
                <span>Sick Leave</span>
                <span>5 / 10 Days</span>
              </div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-yellow-400 w-[50%]" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            >
              <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                <h3 className="font-bold text-[#11469A]">Request New Leave</h3>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                  <SafeIcon name="X" size={20} />
                </button>
              </div>
              <form onSubmit={handleRequestLeave} className="p-6 space-y-4">
                <div>
                  <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-1.5">Leave Category</label>
                  <select 
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:border-[#11469A] outline-none text-sm"
                    value={formData.type}
                    onChange={e => setFormData({...formData, type: e.target.value})}
                  >
                    <option>Annual Leave</option>
                    <option>Sick Leave</option>
                    <option>Study Leave</option>
                    <option>Compassionate</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-1.5">Start Date</label>
                    <input required type="date" className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:border-[#11469A] outline-none text-sm" value={formData.startDate} onChange={e => setFormData({...formData, startDate: e.target.value})} />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-1.5">End Date</label>
                    <input required type="date" className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:border-[#11469A] outline-none text-sm" value={formData.endDate} onChange={e => setFormData({...formData, endDate: e.target.value})} />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-1.5">Reason / Message</label>
                  <textarea rows={3} className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:border-[#11469A] outline-none text-sm resize-none" value={formData.reason} onChange={e => setFormData({...formData, reason: e.target.value})} placeholder="Provide details..." />
                </div>
                <div className="pt-4 flex gap-3">
                  <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-3 border border-gray-200 text-gray-600 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-gray-50">Cancel</button>
                  <button type="submit" className="flex-1 py-3 bg-[#11469A] text-white rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-[#0d3678] shadow-md">Submit Request</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LeaveManagement;